import { useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import { deleteInvoice, getInvoices, createInvoice, sendToStripe, payInvoice, returnToUnpaid } from "../../services/invoices-service";
import { getPartners } from "../../services/partners-service";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  useColorModeValue,
  useDisclosure,
  useToast,
  Box,
  Input,
  List,
  ListItem,
  Text,
  Select,
  VStack,
  Spinner,
  Center,
} from "@chakra-ui/react";

import { IInvoice } from "../../services/invoices-service";
import { currency, formatDate, InvoiceStatus, ITeam, openUrl } from "@bemap/utils";
import { useTranslation } from "react-i18next";
import { Table, Panel, renderInvoiceStatus, renderInvoiceStatusTag, InvoiceActions, PageHeading } from "@bemap/ui";

const InvoiceList = () => {
  const { t } = useTranslation()
  const toast = useToast()

  const initialInvoice: Partial<IInvoice> = {
    seller_team: 1,
    buyer_team: 0,
    unit_price: 70,
    quantity: 0,
    status: 'open',
  }

  // STATES
  const cancelRef = useRef()
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [withDiscount, setWithDiscount] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete">("new");
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [invoice, setInvoice] = useState<Partial<IInvoice>>(initialInvoice);
  const [buyers, setBuyers] = useState<ITeam[]>([]);
  const [updateData, setUpdateData] = useState<string>("");
  const [showBuyerList, setShowBuyerList] = useState(false);
  const [buyerSearchTerm, setBuyerSearchTerm] = useState("");

  // Filter buyers based on search term and sort ASC
  const filteredBuyers = useMemo(() => {
    return buyers
      .filter(buyer =>
        buyer.name.toLowerCase().includes(buyerSearchTerm.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [buyers, buyerSearchTerm]);

  const selectedBuyer = useMemo(() => {
    return buyers.find(buyer => buyer.id === invoice.buyer_team);
  }, [buyers, invoice.buyer_team]);

  // Calculate invoice total
  const invoiceTotal = useMemo(() => {
    const subtotal = (invoice.unit_price || 0) * (invoice.quantity || 0);
    let discountAmount = 0;

    if (invoice.discount_fixed) {
      discountAmount += invoice.discount_fixed;
    }
    if (invoice.discount_percent) {
      discountAmount += subtotal * (invoice.discount_percent / 100);
    }

    return {
      subtotal,
      discountAmount,
      finalAmount: Math.max(0, subtotal - discountAmount)
    };
  }, [invoice.unit_price, invoice.quantity, invoice.discount_fixed, invoice.discount_percent]);

  const columns = useMemo<ColumnDef<IInvoice, any>[]>(
    () => [
      {
        accessorFn: (row) => renderInvoiceStatus(row.status as InvoiceStatus),
        id: "Status",
        header: "STATUS",
        cell: (info) => renderInvoiceStatusTag(info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.seller?.name,
        id: "Nome do Vendedor",
        header: t('partner.invoices_table_seller'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.buyer?.name,
        id: "Nome do Comprador",
        header: t('partner.invoices_table_buyer'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.amount,
        id: "Valor Total",
        header: t('partner.invoices_table_total'),
        cell: (info) => currency('BRL', info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.unit_price,
        id: "Preço Unitário",
        header: t('partner.invoices_table_unit'),
        cell: (info) => currency('BRL', info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.quantity,
        id: "Quantidade de Créditos",
        header: t('partner.invoices_table_qty'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.created_at,
        id: "Criado em",
        header: t('partner.invoices_table_created'),
        cell: (info) => formatDate(info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions'),
        cell: (row) => {
          return (
            <InvoiceActions
              invoice={row.getValue()}
              stripeAction={() => sendInvoice(row.getValue().id)}
              payAction={() => markAsPaid(row.getValue().id)}
              viewAction={() => openUrl(row.getValue().hosted_invoice_url, true)}
              returnToUnpaidAction={() => returnInvoiceToUnpaid(row.getValue().id)}
            />
          );
        },
      },
    ],
    [t]
  );

  const background = useColorModeValue("transparent", "transparent");
  const textColor = useColorModeValue("#1A202C", "alphaWhite.600");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([
        loadInvoices(),
        loadBuyers()
      ]);
      setIsLoading(false);
    };

    fetchData();
  }, [updateData]);

  // Close data-list when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-buyer-list]')) {
        setShowBuyerList(false);
      }
    };

    if (showBuyerList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBuyerList]);

  function closeModal() {
    onClose()
    setInvoice(initialInvoice)
    setWithDiscount(false)
  }

  async function loadInvoices() {
    try {
      const response = await getInvoices();
      if (response.statusCode === 200) {
        setInvoices(response.message);
      }
    } catch (error) {
      setInvoices([]);
    }
  }

  async function sendInvoice(id: number) {
    toast({
      title: 'Carregando...',
      status: 'loading',
      isClosable: true,
    })
    try {
      const response = await sendToStripe(id);
      if (response.statusCode === 200) {
        setUpdateData(new Date().toLocaleString())
        toast.closeAll()
        openUrl(response.message, true);
      }
    } catch (error) {
      toast.closeAll()
      toast({
        title: 'Erro',
        status: 'error',
        isClosable: true,
      })
    }
  }

  async function markAsPaid(id: number) {
    toast({
      title: 'Carregando...',
      status: 'loading',
      isClosable: true,
    })
    try {
      const response = await payInvoice(id);
      if (response.statusCode === 200) {
        setUpdateData(new Date().toLocaleString())
        toast.closeAll()
        toast({
          title: 'Sucesso',
          description: 'Fatura marcada como paga',
          status: 'success',
          isClosable: true,
        })
      }
    } catch (error) {
      toast.closeAll()
      toast({
        title: 'Erro',
        status: 'error',
        isClosable: true,
      })
    }
  }

  async function returnInvoiceToUnpaid(id: number) {
    toast({
      title: 'Carregando...',
      status: 'loading',
      isClosable: true,
    })
    try {
      const response = await returnToUnpaid(id);
      if (response.statusCode === 200) {
        setUpdateData(new Date().toLocaleString())
        toast.closeAll()
        toast({
          title: 'Sucesso',
          description: 'Pagamento desfeito',
          status: 'success',
          isClosable: true,
        })
      }
    } catch (error) {
      toast.closeAll()
      toast({
        title: 'Erro',
        status: 'error',
        isClosable: true,
      })
    }
  }

  async function loadBuyers() {
    try {
      const response = await getPartners();
      if (response.statusCode === 200) {
        setBuyers(response.message);
      }
    } catch (error) {
      setBuyers([]);
    }
  }

  async function createNewInvoice() {
    setIsLoading(true);
    setIsSaving(true);
    toast({
      title: 'Salvando...',
      status: 'loading',
      isClosable: true,
    })
    try {
      const response = await createInvoice(invoice);
      if (response.statusCode === 200) {
        toast.closeAll()
        toast({
          title: 'Sucesso',
          description: 'Fatura criada com sucesso',
          status: 'success',
          isClosable: true,
        })
        setIsLoading(false)
        setIsSaving(false)
        closeModal()
        setUpdateData(new Date().toLocaleString())
        setInvoice(initialInvoice)
      }
    } catch (error) {
      toast.closeAll();
      toast({
        title: 'Erro',
        status: 'error',
        isClosable: true,
      })
      setIsLoading(false);
      setIsSaving(false);
    }
  }

  // const handleDeleteClick = (invoice: IInvoice) => {
  //   setInvoice(invoice);
  //   setModalType('delete');
  //   onOpen();
  // };

  const handleDeleteInvoice = async () => {
    if (!invoice.id) return;

    toast({
      title: 'Excluindo...',
      status: 'loading',
      isClosable: true,
    })
    try {
      const response = await deleteInvoice(invoice.id);
      if (response.statusCode === 200) {
        setUpdateData(new Date().toLocaleString())
        closeModal()
        toast.closeAll()
        toast({
          title: 'Sucesso',
          description: 'Fatura excluída com sucesso',
          status: 'success',
          isClosable: true,
        })
      }
    } catch (error) {
      toast.closeAll()
      toast({
        title: 'Erro',
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  };

  async function handleChange(event: any) {
    setInvoice({
      ...invoice,
      [event.target.name]: event.target.value,
    });
  }

  function RenderModal() {
    switch (modalType) {
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Nova Fatura</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"}>
                  <FormControl mb={4}>
                    <FormLabel>Preço Unitário</FormLabel>
                    <Input
                      name="unit_price"
                      placeholder="55"
                      type="number"
                      min="55"
                      onChange={handleChange}
                      value={invoice.unit_price}
                      required
                    />
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel>Quantidade de Créditos</FormLabel>
                    <Input
                      name="quantity"
                      placeholder="Ex: 5"
                      type="number"
                      min="5"
                      onChange={handleChange}
                      value={invoice.quantity}
                      required
                    />
                  </FormControl>

                  {/* Data-list component for buyer selection */}
                  <Box position="relative" data-buyer-list mb={4}>
                    <Text mb={2} fontSize="sm" fontWeight="medium" color={textColor}>
                      Parceiro
                    </Text>
                    <Input
                      name="buyer_team"
                      placeholder="Clique para selecionar"
                      value={selectedBuyer?.name || ""}
                      onClick={() => setShowBuyerList(!showBuyerList)}
                      readOnly
                      required
                    />

                    {showBuyerList && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        boxShadow="lg"
                        zIndex={1000}
                        maxH="200px"
                        overflowY="auto"
                      >
                        <Input
                          placeholder="Buscar..."
                          value={buyerSearchTerm}
                          onChange={(e) => setBuyerSearchTerm(e.target.value)}
                          border="none"
                          borderRadius="0"
                          borderBottom="1px solid"
                          borderBottomColor="gray.200"
                          _focus={{ boxShadow: "none" }}
                        />
                        <List spacing={0}>
                          {filteredBuyers.map((buyer) => (
                            <ListItem
                              key={buyer.id}
                              px={4}
                              py={2}
                              cursor="pointer"
                              _hover={{ bg: "gray.100" }}
                              onClick={() => {
                                setInvoice({
                                  ...invoice,
                                  buyer_team: buyer.id
                                });
                                setShowBuyerList(false);
                                setBuyerSearchTerm("");
                              }}
                            >
                              <Text>{buyer.name}</Text>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>

                  <FormControl display='flex' alignItems='center' gap="20px" mb={4}>
                    <Switch id='has_discount' colorScheme={'blue'} onChange={() => setWithDiscount(!withDiscount)} />
                    <FormLabel htmlFor='has_discount' mb='0'>
                      Aplicar Desconto
                    </FormLabel>
                  </FormControl>

                  {withDiscount && (
                    <FormControl mb={4}>
                      <FormLabel>Tipo de Desconto</FormLabel>
                      <Select
                        color={textColor}
                        background={background}
                        name="discount_type"
                        defaultValue="default"
                        onChange={handleChange}
                      >
                        <option value="default" disabled hidden>
                          Clique para selecionar
                        </option>
                        <option value='fixed'>
                          Valor Fixo
                        </option>
                        <option value="percent">
                          Percentual
                        </option>
                      </Select>
                    </FormControl>
                  )}

                  {withDiscount && invoice?.discount_type && (
                    <FormControl mb={4}>
                      <FormLabel>Valor do Desconto</FormLabel>
                      <Input
                        color={textColor}
                        background={background}
                        name={invoice?.discount_type === "percent" ? "discount_percent" : "discount_fixed"}
                        type="number"
                        min="1"
                        onChange={handleChange}
                        required
                      />
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        {invoice?.discount_type === "percent" ? "Digite o percentual (ex: 10 para 10%)" : "Digite o valor em reais"}
                      </Text>
                    </FormControl>
                  )}
                </Flex>

                {/* Total calculation display */}
                {invoice.quantity && invoice.quantity > 0 && (
                  <Box mt={6} p={4} bg="gray.50" borderRadius="md">
                    <Text fontSize="lg" fontWeight="bold" mb={3}>
                      Resumo
                    </Text>
                    <Flex direction="column" gap={2}>
                      <Flex justify="space-between">
                        <Text>Preço Unitário:</Text>
                        <Text>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(invoice.unit_price || 0)}</Text>
                      </Flex>
                      <Flex justify="space-between">
                        <Text>Quantidade:</Text>
                        <Text>{invoice.quantity}</Text>
                      </Flex>
                      <Flex justify="space-between" borderTop="1px solid" borderColor="gray.200" pt={2}>
                        <Text fontWeight="medium">Subtotal:</Text>
                        <Text fontWeight="medium">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(invoiceTotal.subtotal)}</Text>
                      </Flex>
                      {invoiceTotal.discountAmount > 0 && (
                        <Flex justify="space-between" color="red.500">
                          <Text>Desconto:</Text>
                          <Text>-{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(invoiceTotal.discountAmount)}</Text>
                        </Flex>
                      )}
                      <Flex justify="space-between" borderTop="1px solid" borderColor="gray.300" pt={2}>
                        <Text fontWeight="bold" fontSize="lg">Total:</Text>
                        <Text fontWeight="bold" fontSize="lg">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(invoiceTotal.finalAmount)}</Text>
                      </Flex>
                    </Flex>
                  </Box>
                )}
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  Cancelar
                </Button>
                <Button
                  colorScheme={"blue"}
                  onClick={createNewInvoice}
                  isDisabled={!invoice.buyer_team || !invoice.quantity || invoice.quantity < 1}
                  isLoading={isSaving}
                >
                  Salvar
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef as unknown as React.RefObject<HTMLElement>}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  Excluir Fatura
                </AlertDialogHeader>

                <AlertDialogBody>
                  Tem certeza que deseja excluir esta fatura? Esta ação não pode ser desfeita.
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef as unknown as React.RefObject<HTMLButtonElement>} onClick={closeModal}>
                    Cancelar
                  </Button>
                  <Button colorScheme='red' onClick={handleDeleteInvoice} ml={3}>
                    Excluir
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap="20px" w="100%" align="stretch">
        <PageHeading
          title={'Invoices'}
          actions={[
            {
              label: t('partner.invoices_new'),
              icon: <AddIcon />,
              onClick: () => {
                onOpen();
                setModalType("new");
              }
            }
          ]}
        />
        <Panel>
          <Table
            tableId="admin-invoices-table"
            columns={columns}
            data={invoices}
          />
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default InvoiceList; 